import { z } from "zod";

// Helper function to validate and format URLs
const validateAndFormatUrl = (url: string, fieldName: string) => {
  if (!url || url.trim() === "") return url;

  let formattedUrl = url.trim();

  // If URL doesn't start with http:// or https://, add https://
  if (
    !formattedUrl.startsWith("http://") &&
    !formattedUrl.startsWith("https://")
  ) {
    formattedUrl = "https://" + formattedUrl;
  }

  try {
    new URL(formattedUrl);
    return formattedUrl;
  } catch {
    throw new Error(
      `${fieldName} must be a valid URL. Please include the full address (e.g., "example.com" or "https://example.com")`,
    );
  }
};

// Custom URL transformer for Zod
const urlTransformer = z.string().transform((val, ctx) => {
  if (!val || val.trim() === "") return val;

  try {
    return validateAndFormatUrl(val, "Website");
  } catch (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: error instanceof Error ? error.message : "Invalid URL format",
    });
    return z.NEVER;
  }
});

export const formSchema = z.object({
  // Basic Information
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  title: z.string().optional(),
  organizationName: z.string().optional(),
  headshot: z
    .any()
    .refine(
      (value) => {
        if (value === null || value === undefined) return false;
        if (value instanceof File) return true;
        if (typeof value === "string" && value.length > 0) return true;
        return false;
      },
      {
        message: "Please upload a headshot",
      },
    )
    .nullable(),
  businessLogo: z
    .any()
    .refine((value) => {
      if (value === null || value === undefined) return true;
      if (value instanceof File) return true;
      if (typeof value === "string" && value.length > 0) return true;
      return true;
    })
    .nullable()
    .optional(),
  wasReferred: z.boolean().optional(),
  referralName: z.string().optional(),

  // Contact Information
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Phone number must have at least 10 digits"),
  website: urlTransformer.optional(),
  address: z
    .object({
      streetAddress1: z.string().optional(),
      streetAddress2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zipCode: z.string().optional(),
    })
    .optional(),

  // Social Media - dynamic object with any social media platform
  socialMedia: z
    .record(
      z.string(),
      z.string().transform((val, ctx) => {
        if (!val || val.trim() === "") return val;

        try {
          return validateAndFormatUrl(val, "Social Media");
        } catch (error) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              error instanceof Error ? error.message : "Invalid URL format",
          });
          return z.NEVER;
        }
      }),
    )
    .optional()
    .refine((val) => {
      // Allow empty objects or objects with valid social media entries
      if (!val || Object.keys(val).length === 0) return true;
      return true; // The individual URL validation is handled by the transform above
    }),

  // About & Services
  aboutMeText: z.string().optional(),
  service: z.string().optional(),

  // Custom Buttons
  customButtons: z
    .array(
      z.object({
        id: z.number(),
        link: urlTransformer.optional(),
        text: z.string().optional(),
        linkId: z.string(),
        textId: z.string(),
      }),
    )
    .optional(),

  // Reviews
  reviews: z
    .array(
      z.object({
        id: z.number(),
        name: z.string().optional(),
        title: z.string().optional(),
        quote: z.string().optional(),
        image: z
          .any()
          .refine((value) => {
            if (value === null || value === undefined) return true;
            if (value instanceof File) return true;
            if (typeof value === "string" && value.length > 0) return true;
            return true;
          })
          .nullable()
          .optional(),
        imageId: z.string().optional(),
        imageName: z.string().optional(),
        stars: z
          .number()
          .int({ message: "Stars must be a whole number" })
          .min(1, { message: "Minimum 1 star" })
          .max(5, { message: "Maximum 5 stars" })
          .optional()
          .default(3),
      }),
    )
    .optional(),

  // Customization
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  buttonColor: z.string().optional(),
  colorScheme: z
    .object({
      primary: z
        .string()
        .regex(/^#[0-9A-F]{6}$/i, "Please enter a valid hex color")
        .optional(),
      secondary: z
        .string()
        .regex(/^#[0-9A-F]{6}$/i, "Please enter a valid hex color")
        .optional(),
      button: z
        .string()
        .regex(/^#[0-9A-F]{6}$/i, "Please enter a valid hex color")
        .optional(),
    })
    .optional(),
  qrCodeLogo: z
    .any()
    .refine((value) => {
      if (value === null || value === undefined) return true;
      if (value instanceof File) return true;
      if (typeof value === "string" && value.length > 0) return true;
      return true;
    })
    .nullable()
    .optional(),
  fabButtonLogo: z
    .any()
    .refine((value) => {
      if (value === null || value === undefined) return true;
      if (value instanceof File) return true;
      if (typeof value === "string" && value.length > 0) return true;
      return true;
    })
    .nullable()
    .optional(),
  faviconLogo: z
    .any()
    .refine((value) => {
      if (value === null || value === undefined) return true;
      if (value instanceof File) return true;
      if (typeof value === "string" && value.length > 0) return true;
      return true;
    })
    .nullable()
    .optional(),
  supportedLanguages: z.array(z.string()).optional(),

  // Template
  selectedTemplate: z.string().optional(),
  templateExhibitId: z.string().optional(),
  template: z.string().optional(),
  areaId: z.string().optional(),
  orgId: z.string().optional(),
  templatePath: z.string().optional(),

  // Generated fields
  addContact: z.string().optional(),
  textLink: z.string().optional(),
  callLink: z.string().optional(),
  sharingMetadata: z.string().optional(),
  isEditing: z.boolean().optional(),
});

export type FormSchema = z.infer<typeof formSchema>;

// Step-specific validation schemas
export const step1Schema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  title: z.string().optional(),
  organizationName: z.string().optional(),
  headshot: z
    .any()
    .refine(
      (value) => {
        if (value === null || value === undefined) return false;
        if (value instanceof File) return true;
        if (typeof value === "string" && value.length > 0) return true;
        return false;
      },
      {
        message: "Please upload a headshot",
      },
    )
    .nullable(),
  businessLogo: z
    .any()
    .refine((value) => {
      if (value === null || value === undefined) return false;
      if (value instanceof File) return true;
      if (typeof value === "string" && value.length > 0) return true;
      return false;
    })
    .nullable()
    .optional(),
  wasReferred: z.boolean().optional(),
  referralName: z.string().optional(),
});

export const step2Schema = z.object({
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Phone number must have at least 10 digits"),
  website: urlTransformer.optional(),
  address: z
    .object({
      streetAddress1: z.string().optional(),
      streetAddress2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      zipCode: z.string().optional(),
    })
    .optional()
    .refine(
      (val) => {
        // Allow undefined or objects with at least one non-empty value
        if (!val) return true;
        return Object.values(val).some((v) => v && v.trim() !== "");
      },
      {
        message: "Address must have at least one field filled",
      },
    ),
});

export const step7Schema = z.object({
  selectedTemplate: z.string().min(1, "Please select a template"),
  templateExhibitId: z.string().optional(),
  template: z.string().optional(),
});

// Validation functions
export const validatePhone = (phone: string): boolean => {
  const digitsOnly = phone.replace(/\D/g, "");
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

export const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");

  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const validateWebsite = (website: string): string => {
  if (!website) return website;
  if (!website.startsWith("http://") && !website.startsWith("https://")) {
    return "https://" + website;
  }
  return website;
};

export const validateSocialLink = (link: string, platform: string): boolean => {
  if (!link) return true;

  const expectedDomains = {
    instagram: ["instagram.com"],
    facebook: ["facebook.com", "fb.com"],
    linkedin: ["linkedin.com"],
    tiktok: ["tiktok.com"],
  };

  const domain = expectedDomains[platform as keyof typeof expectedDomains];
  return domain ? domain.some((d) => link.includes(d)) : true;
};

// Enhanced validation helper for better error messages
export const getFieldValidationError = (
  fieldName: string,
  value: string,
): string | null => {
  if (!value || value.trim() === "") return null;

  // Handle social media fields (dynamic)
  if (fieldName.startsWith("socialMedia.")) {
    try {
      validateAndFormatUrl(value, "Social Media");
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : "Invalid URL format";
    }
  }

  switch (fieldName) {
    case "website":
      try {
        validateAndFormatUrl(value, "Website");
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : "Invalid URL format";
      }
    case "email":
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value)
        ? null
        : "Please enter a valid email address";
    case "phone":
      const digitsOnly = value.replace(/\D/g, "");
      return digitsOnly.length >= 10
        ? null
        : "Phone number must have at least 10 digits";
    default:
      return null;
  }
};
